/*
 * schema-engine.js — the device-settings VJsf product layer (recovered
 * schema-field-type-registry.js): resultSchema normalization (generic display
 * -> custom-* widget + i18n title/description resolution), the x-if / x-fromData
 * expression evaluator, the JSON-Schema-keyword -> Vuetify-rule compiler, and the
 * localized message map + VJsf portlet options.
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (x- keyword branch conditions; the `{{var}}` title interpolation; every
 * validation keyword -> rule mapping; the common.validation.* / common.errors.*
 * message keys; arrayOperations ["create","update","delete"]), internal impl
 * re-authored. The real @koumoul/vjsf internals are far larger — this is the
 * product-specific layer SchemaForm.vue drives when VJsf is unavailable.
 */

import { CUSTOM_WIDGET_REGISTRY } from './field-types.js';
import { t as defaultT } from './i18n.js';

/* ---- small helpers (resolved names va()/Uc) ---- */
export function humanize(s) { return s ? String(s).replace(/_/g, ' ') : s; }
export function cloneDeep(o) { return o == null ? o : JSON.parse(JSON.stringify(o)); }

/* getTitleAndVariables — split "{{var}}" placeholders out of a title token. */
export function getTitleAndVariables(token) {
  const re = /{{([\s\S]+?)}}/g;
  const s = '' + token;
  const variables = (s.match(re) || []).map((a) => a.replace(/[{}]/g, ''));
  return { title: s.replace(re, ''), variables };
}

/**
 * makeResultSchema(rawSchema, ctx) — deep-walk the schema:
 *   - upgrade generic displays (radio/boolean -> custom-radio; time-field ->
 *     custom-time-field; slider -> custom-slider) to the product widgets
 *   - resolve title/description i18n keys against candidate locale roots
 * ctx: { resultLocalePart, localeRoot, $t, $te, getTitleAndVariables? }
 */
export function makeResultSchema(rawSchema, ctx) {
  const $t = ctx.$t || defaultT;
  const $te = ctx.$te || (() => false);
  const splitVars = ctx.getTitleAndVariables || getTitleAndVariables;

  const translate = (token, localePart, localeRoot) => {
    const { title, variables } = splitVars(token);
    const candidates = [title, `${localePart}.${title}`, `${localeRoot}.${title}`];
    for (const key of candidates) {
      if ($te(key)) {
        const out = variables.length ? $t(key, variables) : $t(key);
        return typeof out === 'object' ? (out.label || humanize(title)) : out;
      }
    }
    return humanize(title) || null;
  };

  const upgradeNode = (n, localePart, localeRoot) => {
    if (n['x-display'] === 'radio' && n.type === 'boolean') n['x-display'] = 'custom-radio';
    if (n.description) n.description = translate(n.description, localePart, localeRoot);
    if (n['x-display'] === 'time-field') return Object.assign({}, n, { 'x-display': 'custom-time-field' });
    if (n['x-display'] === 'slider') {
      const ticks = (n['x-props'] && n['x-props'].ticks) || undefined;
      return Object.assign({}, n, {
        'x-display': 'custom-slider',
        'x-props': Object.assign({}, n['x-props'] || {}, {
          ticks: ticks && ticks.map((c) => translate(c, localePart, localeRoot)),
        }),
      });
    }
    return n;
  };

  const walk = (n) => {
    try {
      for (const k in n) {
        if (Array.isArray(n[k])) n[k] = n[k].map(walk);
        else if (n[k] && typeof n[k] === 'object') n[k] = walk(n[k]);
      }
      let a = upgradeNode(n, ctx.resultLocalePart, ctx.localeRoot);
      if (n.title) a = Object.assign({}, a, { title: translate(a.title, ctx.resultLocalePart, ctx.localeRoot) });
      return a;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e, n);
      return n;
    }
  };

  return walk(cloneDeep(rawSchema));
}

/**
 * evalExpr(expr, exprCtx) — compile an x-if / x-fromData JS expression with
 * `new Function` and evaluate it against the model context. `model.<field>`
 * inside the expression reads the current value.
 */
const _exprCache = {};
export function evalExpr(expr, exprCtx) {
  const ctx = Object.assign({}, exprCtx);
  const keys = Object.keys(ctx);
  const fn = _exprCache[expr] || (_exprCache[expr] = new Function(...keys, `return (${expr});`));
  try {
    return fn(...keys.map((k) => ctx[k]));
  } catch (e) {
    return undefined;
  }
}

/** Build the standard expression context (model/root/modelRoot/context). */
export function makeExprCtx(model, options = {}) {
  return { model, root: model, modelRoot: model, context: options.context || {} };
}

/* x-if gating (recovered conditionalVisibility). */
export const conditionalVisibility = {
  hideField(node, model, options) {
    return !!(node && node['x-if']) && !evalExpr(node['x-if'], makeExprCtx(model, options));
  },
  keepOneOf(branch, model, options) {
    return !branch['x-if'] || !!evalExpr(branch['x-if'], makeExprCtx(model, options));
  },
};

/**
 * buildRules(node, options, required) — compile JSON-Schema constraints into
 * Vuetify validators (each returns `true` or a localized message).
 */
export function buildRules(node, options, required) {
  const msg = options.messages;
  const rules = [];
  if (required) rules.push((v) => (v != null && v !== '') || msg.required);

  if (node.type === 'array' && node.minItems !== undefined) {
    const m = msg.minItems.replace('{minItems}', node.minItems);
    rules.push((v) => !v || v.length >= node.minItems || m);
  }
  if (node.type === 'array' && node.maxItems !== undefined) {
    const m = msg.maxItems.replace('{maxItems}', node.maxItems);
    rules.push((v) => !v || v.length <= node.maxItems || m);
  }
  if (node.type === 'string' && node.minLength !== undefined) {
    const m = msg.minLength.replace('{minLength}', node.minLength);
    rules.push((v) => v == null || v.length >= node.minLength || m);
  }
  if (node.type === 'string' && node.maxLength !== undefined) {
    const m = msg.maxLength.replace('{maxLength}', node.maxLength);
    rules.push((v) => v == null || v.length <= node.maxLength || m);
  }
  if (node.type === 'string' && node.patternRegexp !== undefined) {
    const m = msg.pattern.replace('{pattern}', node.pattern);
    rules.push((v) => v == null || v === '' || !!String(v).match(node.patternRegexp) || m);
  }
  if (['number', 'integer'].includes(node.type) && node.maximum !== undefined) {
    const m = msg.maximum.replace('{maximum}', node.maximum);
    rules.push((v) => v == null || v <= node.maximum || m);
  }
  if (['number', 'integer'].includes(node.type) && node.exclusiveMaximum !== undefined) {
    const m = msg.exclusiveMaximum.replace('{exclusiveMaximum}', node.exclusiveMaximum);
    rules.push((v) => v == null || v < node.exclusiveMaximum || m);
  }
  if (['number', 'integer'].includes(node.type) && node.minimum !== undefined) {
    const m = msg.minimum.replace('{minimum}', node.minimum);
    rules.push((v) => v == null || v >= node.minimum || m);
  }
  if (['number', 'integer'].includes(node.type) && node.exclusiveMinimum !== undefined) {
    const m = msg.exclusiveMinimum.replace('{exclusiveMinimum}', node.exclusiveMinimum);
    rules.push((v) => v == null || v > node.exclusiveMinimum || m);
  }
  return rules;
}

/** buildMessages($t) — the VJsf options.messages map (recovered `bZ`). */
export function buildMessages($t) {
  const T = $t || defaultT;
  return {
    required: T('common.validation.required'),
    noData: T('common.errors.201'),
    search: T('common.service.search'),
    minimum: T('common.validation.minValue', { min: '{minimum}' }),
    exclusiveMinimum: T('common.validation.minValue', { min: '{exclusiveMinimum}' }),
    maximum: T('common.validation.maxValue', { max: '{maximum}' }),
    exclusiveMaximum: T('common.validation.maxValue', { max: '{exclusiveMaximum}' }),
    minLength: T('common.validation.minLength', { min: '{minLength}' }),
    maxLength: T('common.validation.maxLength', { max: '{maxLength}' }),
    minItems: T('common.validation.minChecked', { min: '{minItems}' }),
    maxItems: T('common.validation.maxChecked', { max: '{maxItems}' }),
    pattern: T('common.validation.generic'),
    selectAll: T('common.tracker_list.toolbar.hints.checkbox'),
    ok: T('common.btn.ok'), cancel: T('common.btn.cancel'),
    openMenu: T('common.openMenu'), delete: T('common.delete'),
    duplicate: T('common.duplicate'), copy: T('common.copy'), paste: T('common.paste'),
  };
}

/** portletVjsfOptions($i18n, $t) — VJsf global options at portlet level. */
export function portletVjsfOptions($i18n, $t) {
  return {
    defaultIcons: true,
    locale: $i18n && $i18n.locale,
    arrayOperations: ['create', 'update', 'delete'],
    messages: buildMessages($t),
  };
}

/** Re-export the widget registry for convenience (owned by field-types.js). */
export { CUSTOM_WIDGET_REGISTRY };

export default {
  makeResultSchema,
  getTitleAndVariables,
  evalExpr,
  makeExprCtx,
  conditionalVisibility,
  buildRules,
  buildMessages,
  portletVjsfOptions,
  humanize,
  cloneDeep,
};
